import { useSyncExternalStore } from 'react';
import { User, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';

export interface UserListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: UserListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useUsers = (params: UserListParams): PaginatedResponse<User> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('users', cb),
    () => 0,
    () => 0
  );
  return ctx.read('users', params, (p: UserListParams) => api.listStaffUsers(buildQuery(p)));
};

export interface UserMutations {
  create: (user: User) => Promise<void>;
  update: (user: Partial<User> & { id: string }) => Promise<void>;
  updateOwnSignature: (userId: string, signature: string) => Promise<void>;
  remove: (userId: string) => Promise<void>;
  resetPassword: (userId: string) => Promise<void>;
}

export const useUserMutations = (): UserMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('users');
  return {
    create: async (user) => {
      await api.createStaffUser(user);
      after();
    },
    update: async (user) => {
      await api.updateUserProfile(user);
      after();
    },
    updateOwnSignature: async (userId, signature) => {
      await api.updateOwnSignature(userId, signature);
      after();
    },
    remove: async (userId) => {
      await api.deleteUser(userId);
      after();
    },
    resetPassword: async (userId) => {
      await api.resetStaffPassword(userId);
    }
  };
};
